/** @jsx jsx */
/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from 'react';
import { jsx } from '@emotion/core';
import Modal from 'components/molecules/Modal';
import { getStaffDetail } from 'api/shiftBoard';
import { StaffDetail } from 'domain/attend/shiftBoard/types';
import { DOW_LABELS, shortDateLabel } from 'domain/attend/shiftBoard/time';
import { boardStyles } from './styles';

export type StaffDetailPanelProps = {
  open: boolean;
  orgCode: string;
  staffCode: string | null;
  targetDate: string;
  /** 希望シフトを出す期間。シフト表の表示期間に合わせる */
  dateFrom: string;
  dateTo: string;
  onClose: () => void;
};

/**
 * スタッフ詳細（曜日設定と希望シフト）
 *
 * 締め期間の集計は 9/13 の決定で取りやめた。勤務日数や人件費はシフト表の行末で見る。
 */
const StaffDetailPanel: React.FC<StaffDetailPanelProps> = ({
  open, orgCode, staffCode, targetDate, dateFrom, dateTo, onClose,
}) => {
  const [detail, setDetail] = useState<StaffDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !staffCode) {
      return;
    }
    setDetail(null);
    setError(null);
    getStaffDetail(orgCode, staffCode, targetDate, dateFrom, dateTo).then(setDetail).catch(() => setError('取得できませんでした。'));
  }, [open, orgCode, staffCode, targetDate, dateFrom, dateTo]);

  return (
    <Modal open={open} closeHandler={onClose} title={detail ? detail.staff.staffName : 'スタッフ詳細'} submitText="閉じる" onSubmit={onClose} isShowButtonClose={false} width="480px">
      <div css={boardStyles.modalBody}>
        {error && <div css={boardStyles.dangerText}>{error}</div>}
        {!detail && !error && <div css={boardStyles.subText}>読み込み中…</div>}
        {detail && (
          <React.Fragment>
            {/* 雇用形態名は権限で出ないことがあるので、空の項目は区切りごと出さない */}
            <div css={boardStyles.subText}>
              {[detail.staff.belongOrgName, detail.staff.executiveName, detail.staff.employmentName, detail.staff.mainBusinessName].filter(Boolean).join(' / ')}
            </div>
            <div style={{ marginTop: '10px', fontWeight: 600 }}>曜日設定</div>
            {detail.weekPatterns.length === 0 && <div css={boardStyles.subText}>未設定</div>}
            <div css={boardStyles.formRow}>
              {detail.weekPatterns.map((w) => (
                <span key={w.dow} css={boardStyles.subText}>
                  {`${w.dow === 7 ? '祝' : DOW_LABELS[w.dow]}: ${w.shiftPatternName || (w.holidayId ? (w.isLegal ? '公休(法)' : '公休') : '-')}`}
                </span>
              ))}
            </div>
            <div style={{ marginTop: '10px', fontWeight: 600 }}>{`希望シフト（${shortDateLabel(dateFrom)} 〜 ${shortDateLabel(dateTo)}）`}</div>
            {detail.hopeShifts.length === 0 && <div css={boardStyles.subText}>提出なし</div>}
            <div style={{ maxHeight: '160px', overflowY: 'auto' }}>
              {detail.hopeShifts.map((h) => (
                <div key={h.hopeShiftId} css={boardStyles.subText}>
                  {`${shortDateLabel(h.targetDate)}: ${h.type === 2 ? '休み希望' : h.start ? `${h.start}-${h.end}` : 'パターン希望'}${h.note ? `（${h.note}）` : ''}`}
                </div>
              ))}
            </div>
          </React.Fragment>
        )}
      </div>
    </Modal>
  );
};

export default StaffDetailPanel;
